import React from 'react';
import clsx from 'clsx';
import Popover from '@material-ui/core/Popover';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import { Divider, ListSubheader } from '@material-ui/core';
import { PopoverOrigin } from '@material-ui/core/Popover/Popover';

interface MenuItem {
    disabled?: boolean;
    onClick?: () => void;
    id?: string;
    text: string;
    icon?: any;
}

export type ContextualMenuItem = MenuItem;

export interface ContextualMenuLevelItem {
    items: MenuItem[];
    name: string | null;
}

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    paper: {
        borderRadius: theme.spacing(1),
    },
    itemRoot: {
        paddingTop: 0,
        paddingBottom: 0,
        height: theme.spacing(4),
        '&:hover': {},
    },
    subItem: {
        paddingLeft: theme.spacing(32 / 8),
    },
    itemPrimary: {},
    disabledItem: {
        backgroundColor: `${'transparent'} ${'!important'}`,
    },
    disabledText: {},
    subheader: {
        height: theme.spacing(32 / 8),
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
        textTransform: 'uppercase',
        lineHeight: theme.spacing(12 / 8),
        letterSpacing: theme.spacing(0.4 / 8),
        marginTop: theme.spacing(4 / 8),
    },
    divider: {},
}));

interface PropsBasic {
    anchorElement: HTMLElement | null;
    anchorOrigin?: PopoverOrigin;
    handleClose: () => void;
    transformOrigin?: PopoverOrigin;
    indexPrefix?: string;
}

interface PropsSingleLevel extends PropsBasic {
    multiLevelList?: undefined;
    singleLevelList: MenuItem[];
}

interface PropsMultiLevel extends PropsBasic {
    multiLevelList: ContextualMenuLevelItem[];
    singleLevelList?: undefined;
}

interface InternalList {
    items: MenuItem[];
    name: string | null;
}

type Props = PropsMultiLevel | PropsSingleLevel;

const ContextualMenu: React.FC<Props> = (props) => {
    const {
        anchorElement,
        anchorOrigin = {
            vertical: 'top',
            horizontal: 'right',
        },
        handleClose,
        multiLevelList,
        singleLevelList,
        transformOrigin = {
            vertical: 'top',
            horizontal: 'right',
        },
        indexPrefix,
    } = props;

    let list: InternalList[] = [];
    if (multiLevelList !== undefined) {
        list = multiLevelList;
    }
    if (singleLevelList !== undefined) {
        list = [
            {
                name: null,
                items: singleLevelList,
            },
        ];
    }

    const classes = useStyles();

    return (
        <div className={classes.root}>
            <Popover
                id="simple-popover"
                open={anchorElement !== null}
                anchorEl={anchorElement}
                onClose={(event: React.SyntheticEvent): void => {
                    event.stopPropagation();
                    handleClose();
                }}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                classes={{
                    paper: classes.paper,
                }}
            >
                {list.map(({ name, items }, index) => (
                    <div key={(indexPrefix ?? '') + 'List' + index.toString()}>
                        {index !== 0 && (
                            <Divider classes={{ root: classes.divider }} />
                        )}
                        <List
                            component="nav"
                            subheader={
                                name === null ? undefined : (
                                    <ListSubheader
                                        component="div"
                                        classes={{ root: classes.subheader }}
                                    >
                                        {name}
                                    </ListSubheader>
                                )
                            }
                            onClick={(
                                event: React.MouseEvent<HTMLElement>
                            ): void => event.stopPropagation()}
                        >
                            {items.map(
                                ({ disabled, onClick, text, id, icon }) => (
                                    <ListItem
                                        onClick={(event): void => {
                                            event.stopPropagation();
                                            onClick && onClick();
                                        }}
                                        button
                                        classes={{
                                            root: clsx(
                                                classes.itemPrimary,
                                                disabled && classes.disabledText
                                            ),
                                            disabled: classes.disabledItem,
                                        }}
                                        key={
                                            (indexPrefix ?? '') +
                                            'Item' +
                                            (id ?? text)
                                        }
                                        disabled={disabled}
                                    >
                                        {icon && icon}
                                        <ListItemText
                                            primary={text}
                                            classes={{
                                                primary:
                                                    disabled === true
                                                        ? clsx(
                                                              classes.itemPrimary,
                                                              classes.disabledText
                                                          )
                                                        : classes.itemPrimary,
                                            }}
                                        />
                                    </ListItem>
                                )
                            )}
                        </List>
                    </div>
                ))}
            </Popover>
        </div>
    );
};

export default ContextualMenu;
